import { Mail, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCreateInvitation, useDeleteInvitation, useInvitations, useSettings, useTemplates } from '../../api/hooks'
import type { Event, Template } from '../../api/types'
import { Badge, Button, Card, DrillLink, EmptyState, Modal, Spinner } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { fmtDateTime, fmtRelative, INV_STATUS_LABEL, num, pct } from '../../lib/format'

const TONE = { UTKAST: 'neutral', SCHEMALAGD: 'warning', SKICKAS: 'navy', SKICKAT: 'success' } as const

export function InvitationsTab({ event: e }: { event: Event }) {
  const invs = useInvitations(e.id)
  const templates = useTemplates()
  const settings = useSettings()
  const create = useCreateInvitation(e.id)
  const remove = useDeleteInvitation(e.id)
  const nav = useNavigate()
  const toast = useToast()
  const [picker, setPicker] = useState(false)
  const list = invs.data ?? []

  const start = (t?: Template) =>
    create.mutate(
      {
        title: t ? `Inbjudan (${t.name.toLowerCase()})` : 'Ny inbjudan',
        subject: t?.subject ?? 'Inbjudan: {{event_namn}}',
        preheader: t?.preheader ?? null,
        sender_name: settings.data?.mail_default_sender_name ?? 'Söderberg & Partners',
        sender_email: settings.data?.mail_default_sender_email ?? 'event@soderbergpartners.se',
        blocks: t?.blocks ?? [{ type: 'heading', text: 'Välkommen till {{event_namn}}' }, { type: 'text', text: 'Hej {{förnamn}},\n\n' }, { type: 'event_details' }, { type: 'cta', label: 'Anmäl dig här' }],
        template_key: t?.key ?? null,
        include_kund: true,
        include_prospekt: true,
      },
      { onSuccess: (inv) => { setPicker(false); nav(`/event/${e.id}/inbjudan/${inv.id}`) } },
    )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">Bygg inbjudan en gång. Vid utskick får varje mottagare automatiskt sitt teams anmälningslänk och sin rådgivares namn.</p>
        <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => setPicker(true)} disabled={e.counts.categories === 0} title={e.counts.categories === 0 ? 'Generera kategorier först' : ''}>Ny inbjudan</Button>
      </div>

      {invs.isLoading ? <Spinner /> : list.length === 0 ? (
        <EmptyState icon={<Mail className="size-8" />} title="Ingen inbjudan ännu" description={e.counts.categories === 0 ? 'Generera kategorier först, så att varje mottagare kan få rätt anmälningslänk.' : 'Utgå från en mall eller börja från ett tomt blad.'} action={<Button variant="primary" onClick={() => setPicker(true)} disabled={e.counts.categories === 0}>Skapa inbjudan</Button>} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((i, idx) => (
            <Card key={i.id} className="animate-fade-up transition-shadow duration-[140ms] hover:shadow-(--shadow-card-hover)" style={{ animationDelay: `${idx * 50}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Badge tone={TONE[i.status]} dot>{INV_STATUS_LABEL[i.status]}</Badge>{i.template_key && <span className="text-xs text-ink-400">Mall: {i.template_key}</span>}</div>
                  <h3 className="mt-2 truncate text-base">{i.title}</h3>
                  <p className="truncate text-sm text-ink-500">Ämne: {i.subject}</p>
                </div>
                {i.status === 'UTKAST' && (
                  <button className="rounded p-1 text-ink-400 hover:bg-danger-50 hover:text-danger-600" title="Radera utkast" onClick={() => remove.mutate(i.id, { onSuccess: () => toast.push('info', 'Utkastet raderades') })}><Trash2 className="size-4" /></button>
                )}
              </div>
              {i.status === 'SKICKAT' || i.status === 'SKICKAS' ? (
                <div className="mt-4 grid grid-cols-4 gap-2 text-sm">
                  <div><div className="text-xs text-ink-500">Skickade</div><div className="font-semibold tabular-nums"><DrillLink to={`/event/${e.id}/utskick?invitation_id=${i.id}`}>{num(i.sent_count)}</DrillLink></div></div>
                  <div><div className="text-xs text-ink-500">Öppnade</div><div className="font-semibold tabular-nums"><DrillLink to={`/event/${e.id}/utskick?invitation_id=${i.id}&filter=opened`}>{i.sent_count ? pct(i.opened_count / i.sent_count) : '–'}</DrillLink></div></div>
                  <div><div className="text-xs text-ink-500">Klickade</div><div className="font-semibold tabular-nums"><DrillLink to={`/event/${e.id}/utskick?invitation_id=${i.id}&filter=clicked`}>{i.sent_count ? pct(i.clicked_count / i.sent_count) : '–'}</DrillLink></div></div>
                  <div><div className="text-xs text-ink-500">Anmälda</div><div className="font-semibold tabular-nums text-navy-800"><DrillLink to={`/event/${e.id}/utskick?invitation_id=${i.id}&filter=registered`}>{num(i.registered_count)}</DrillLink></div></div>
                </div>
              ) : (
                <p className="mt-4 text-xs text-ink-500">{i.status === 'SCHEMALAGD' && i.scheduled_at ? `Skickas ${fmtDateTime(i.scheduled_at)}` : `Uppdaterad ${fmtRelative(i.updated_at)} · ${i.include_kund && i.include_prospekt ? 'kunder och prospekt' : i.include_kund ? 'endast kunder' : 'endast prospekt'}`}</p>
              )}
              <div className="mt-4 flex justify-end">
                <Link to={`/event/${e.id}/inbjudan/${i.id}`}><Button size="sm" variant={i.status === 'UTKAST' ? 'primary' : 'secondary'}>{i.status === 'UTKAST' ? 'Fortsätt redigera' : 'Öppna'}</Button></Link>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={picker} onClose={() => setPicker(false)} title="Välj utgångspunkt" wide>
        <div className="grid gap-3 md:grid-cols-3">
          {templates.data?.map((t) => (
            <button key={t.key} onClick={() => start(t)} className="card-interactive p-4 text-left">
              <div className="text-sm font-semibold text-ink-900">{t.name}</div>
              <p className="mt-1 text-xs text-ink-500">{t.description}</p>
              <p className="mt-3 truncate text-xs text-ink-400">Ämne: {t.subject}</p>
            </button>
          ))}
          <button onClick={() => start()} className="rounded-(--radius-card) border border-line-200 bg-navy-50/40 p-4 text-left hover:border-navy-300">
            <div className="text-sm font-semibold text-ink-900">Tomt blad</div>
            <p className="mt-1 text-xs text-ink-500">Rubrik, text, eventdetaljer och knapp – fyll i själv.</p>
          </button>
        </div>
      </Modal>
    </div>
  )
}
